import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import socialLinks from "../../../assets/data/socials";

const NAV_ITEMS = [
  { label: "Home", id: "#hero" },
  { label: "Work", id: "#work" },
  { label: "Services", id: "#services" },
  { label: "About", id: "#about" },
  { label: "Contact", id: "#contact" },
];

const drawer = {
  initial: { opacity: 0, y: -10, scale: 0.97 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.32,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.05,
      delayChildren: 0.06,
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.97,
    transition: {
      duration: 0.22,
      ease: [0.4, 0, 1, 1],
      staggerChildren: 0.03,
      staggerDirection: -1,
    },
  },
};

const listItem = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, y: -4, transition: { duration: 0.15, ease: [0.4, 0, 1, 1] } },
};

function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  const prevRef = useRef(ids[0]);

  useEffect(() => {
    const els = ids.map((id) => document.querySelector(id)).filter(Boolean);
    if (!els.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) {
          const id = "#" + visible[0].target.id;
          if (id !== prevRef.current) {
            prevRef.current = id;
            setActive(id);
          }
        }
      },
      { threshold: 0.25, rootMargin: "0px 0px -20% 0px" }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [ids]);

  return active;
}

export default function Sidebar({ isOpen, onClose, scrollTo, anchorRef }) {
  const sectionIds = NAV_ITEMS.map((n) => n.id);
  const activeSection = useActiveSection(sectionIds);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const drawerRef = useRef(null);

  /* close on outside click */
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (
        drawerRef.current &&
        !drawerRef.current.contains(e.target) &&
        anchorRef?.current &&
        !anchorRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, onClose, anchorRef]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={drawerRef}
          key="drawer"
          variants={drawer}
          initial="initial"
          animate="animate"
          exit="exit"
          className="fixed top-[64px] right-3 md:right-8 z-40 w-[calc(100vw-1.5rem)] sm:max-w-xs md:max-w-xs rounded-2xl overflow-hidden"
          style={{
            background: "rgba(18,18,18,0.92)",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            border: "1px solid rgba(245,244,240,0.1)",
            boxShadow: "0 24px 48px rgba(0,0,0,0.5)",
          }}
        >
          {/* nav links */}
          <nav className="flex flex-col px-4 md:px-5 pt-5 pb-3 gap-1 md:gap-0.5">
            {NAV_ITEMS.map((navItem, i) => {
              const isActive = activeSection === navItem.id;
              const isHovered = hoveredIdx === i;
              const isDimmed = hoveredIdx !== null && !isHovered;

              return (
                <motion.button
                  key={navItem.id}
                  variants={listItem}
                  onClick={() => scrollTo(navItem.id)}
                  onMouseEnter={() => setHoveredIdx(i)}
                  onMouseLeave={() => setHoveredIdx(null)}
                  className="flex items-center justify-between w-full px-3 py-3 md:py-2.5 rounded-xl text-left"
                  style={{
                    background: isActive ? "rgba(245,244,240,0.06)" : "transparent",
                    transition: "background 0.18s ease",
                  }}
                >
                  {/* inner wrapper carries the dim so framer opacity isn't clobbered */}
                  <span
                    className="font-Montreal-Bold text-2xl md:text-xl text-cream leading-none"
                    style={{
                      opacity: isDimmed ? 0.3 : 1,
                      transition: "opacity 0.18s ease",
                    }}
                  >
                    {navItem.label}
                  </span>

                  <div
                    className="flex items-center gap-2"
                    style={{
                      opacity: isDimmed ? 0.3 : 1,
                      transition: "opacity 0.18s ease",
                    }}
                  >
                    <AnimatePresence>
                      {isActive && (
                        <motion.span
                          className="w-1.5 h-1.5 rounded-full"
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          style={{ background: "#F5F4F0" }}
                        />
                      )}
                    </AnimatePresence>
                    <span
                      className="font-Montreal-Mono text-[10px] tracking-widest"
                      style={{ color: "rgba(245,244,240,0.3)" }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </nav>

          {/* divider */}
          <div style={{ height: 1, background: "rgba(245,244,240,0.08)", margin: "0 20px" }} />

          {/* socials */}
          <div className="flex items-center gap-5 px-8 py-4">
            {socialLinks.map((s) => (
              <a
                key={s.title}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className="font-Montreal-Mono text-[10px] tracking-widest uppercase opacity-30 hover:opacity-80 transition-opacity duration-200"
                style={{ color: "#F5F4F0" }}
              >
                {s.title}
              </a>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
